import { useEffect, useRef } from 'react';

interface JsonLdItem {
  "@context": string;
  "@type": string;
  [key: string]: unknown;
}

interface PageSeoProps {
  title: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  jsonLd?: JsonLdItem[];
}

export default function PageSeo({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  jsonLd,
}: PageSeoProps) {
  const injectedRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    document.title = title;
    return () => {};
  }, [title]);

  useEffect(() => {
    const head = document.head;

    const cleanup: HTMLElement[] = [];

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        head.appendChild(el);
        cleanup.push(el);
      }
      el.setAttribute('content', content);
    };

    if (description) setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonicalUrl) {
      let link = head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        head.appendChild(link);
        cleanup.push(link);
      }
      link.setAttribute('href', canonicalUrl);
    }
    if (ogTitle || title) setMeta('og:title', ogTitle || title, true);
    if (ogDescription || description) setMeta('og:description', ogDescription || description || '', true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) setMeta('og:url', canonicalUrl, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle || title);
    if (ogDescription || description) setMeta('twitter:description', ogDescription || description || '');

    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((item) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(item);
        head.appendChild(script);
        cleanup.push(script);
      });
    }

    injectedRef.current = cleanup;

    return () => {
      injectedRef.current.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      injectedRef.current = [];
    };
  }, []);

  return null;
}